import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useCallback,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../../../methods/api/apiClient";
import loader from "../../../../methods/loader";
import swal from "sweetalert";
import methodModel from "../../../../methods/methods";
import Layout from "../../../../components/global/layout";
import environment from "../../../../environment";
import Checkbox from "../../../../components/checkbox/checkbox";
import SelectDropdown from "../../../../components/common/SelectDropdown";
import shared from "./Shared";
import FormControl from "../../../../components/common/FormControl";
import { set } from "react-hook-form";

const EQUIPMENT_TYPES = [
  "Compressor",
  "Generator",
  "Pump",
  "Motor",
  "Turbine",
  "Dispensers",
  "Other",
];

const emptyForm = {
  productName: "",
  equipmentType: "",
  equipmentTypeId: "",
  manufacturerId: "",
  manufacturer: "",
  model: "",
  modelNumber: [], // multi-tag
  keySpecifications: [], // multi-tag
  description: "",
  image: "",
  // ── Single URL (string) ──
  dataSheet: "",
  brochure: "",
  // ── Multiple URLs (array of strings) ──
  dailyUseGuide: [],
  cleaningGuide: [],
  illustratedParts: [],
  troubleshooting: [],
  partsStore: [],
  // ── PDF uploads (array of downloadUrls) ──
  wiringDiagram: [],
  userManual: [],
  serviceManual: [],
};

// ─── URL validator ────────────────────────────────────────────────
const isValidUrl = (str) => {
  try {
    const url = new URL(str.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

// ─── FormInput ────────────────────────────────────────────────────
const FormInput = ({
  label,
  name,
  type = "text",
  required,
  options,
  textarea,
  value,
  onChange,
  error,
}) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all resize-none ${error ? "border-red-400" : "border-gray-200"}`}
      />
    ) : options ? (
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white ${error ? "border-red-400" : "border-gray-200"}`}
      >
        <option value="">Select {label}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${error ? "border-red-400" : "border-gray-200"}`}
      />
    )}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

// ─── Multi Tag Input (modelNumber & keySpecifications) ────────────
const MultiTagInput = ({
  label,
  name,
  required,
  values = [],
  onChange,
  error,
  placeholder,
}) => {
  const [inputVal, setInputVal] = useState("");

  const addTags = (raw) => {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const merged = [...new Set([...values.map(String), ...parts.map(String)])];
    onChange(merged);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (inputVal.trim()) {
        addTags(inputVal);
        setInputVal("");
      }
    } else if (e.key === "Backspace" && !inputVal && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputVal.trim()) {
      addTags(inputVal);
      setInputVal("");
    }
  };

  const removeTag = (idx) => onChange(values.filter((_, i) => i !== idx));

  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div
        className={`min-h-[42px] w-full border rounded-xl px-3 py-2 flex flex-wrap gap-1.5 items-center cursor-text transition-all bg-white focus-within:ring-2 focus-within:ring-[#115B9D]/30 focus-within:border-[#115B9D] ${error ? "border-red-400" : "border-gray-200"}`}
      >
        {values.map((tag, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 bg-[#115B9D] text-white text-xs px-2 py-1 rounded-lg"
          >
            <span>{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(i)}
              className="hover:bg-white/20 rounded p-0.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={
            values.length === 0
              ? placeholder || "Type and press Enter or comma to add"
              : ""
          }
          className="flex-1 min-w-[120px] text-sm text-gray-800 outline-none bg-transparent"
        />
      </div>
      <p className="text-xs text-gray-600 mt-1">
        Press Enter or comma to add multiple values
      </p>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
};

// ─── Link icon SVG ────────────────────────────────────────────────
const LinkIcon = ({ size = 13 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// ─── Saved URL row (shared by both single & multi link) ───────────
const SavedUrlRow = ({ url, onRemove }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-[#115B9D08] border border-[#115B9D]/15 rounded-xl">
    <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 text-[#115B9D]">
      <LinkIcon />
    </div>
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-xs text-[#115B9D] truncate flex-1 hover:underline"
    >
      {url}
    </a>
    <button
      type="button"
      onClick={onRemove}
      className="size-6 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors shrink-0"
      title="Remove"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
);

// ─── URL input row (shared by both single & multi link) ───────────
const UrlInputRow = ({
  value,
  onChange,
  onAction,
  actionLabel,
  urlError,
  placeholder,
  onBlur = () => {},
}) => (
  <div className="space-y-1">
    <div className="flex gap-2">
      <div className="relative flex-1">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">
          <LinkIcon />
        </div>
        <input
          type="url"
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAction();
            }
          }}
          placeholder={placeholder || "https://example.com"}
          className={`w-full border rounded-xl pl-8 pr-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${urlError ? "border-red-400" : "border-gray-200"}`}
        />
      </div>
      {actionLabel ? (
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 px-3 py-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-medium rounded-xl transition-colors flex items-center gap-1"
        >
          {actionLabel === "Add" && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          )}
          {actionLabel}
        </button>
      ) : null}
    </div>
    {urlError && <p className="text-xs text-red-400">{urlError}</p>}
    {actionLabel ? (
      <p className="text-xs text-gray-600">
        Enter a valid URL and press {actionLabel} or Enter
      </p>
    ) : null}
  </div>
);

// ─── Single URL Input (dataSheet, brochure) ───────────────────────
const SingleLinkInput = ({
  label,
  value = "",
  onChange,
  registerPendingCheck,
}) => {
  const [inputVal, setInputVal] = useState(value);
  const [saved, setSaved] = useState(!!value);

  useEffect(() => {
    if (registerPendingCheck) {
      registerPendingCheck(() => null);
    }
  }, [registerPendingCheck]);

  useEffect(() => {
    setInputVal(value || "");
    setSaved(!!value);
  }, [value]);

  const handleSave = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) {
      return;
    }
    setSaved(true);
    onChange(trimmed);
  };

  const handleRemove = () => {
    setInputVal("");
    setSaved(false);
    onChange("");
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm text-gray-700">{label}</label>
      {saved ? (
        <SavedUrlRow url={inputVal} onRemove={handleRemove} />
      ) : (
        <UrlInputRow
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={handleSave}
          onAction={handleSave}
          actionLabel=""
          placeholder="https://example.com/datasheet"
        />
      )}
    </div>
  );
};

// ─── Multi URL Input (dailyUseGuide, cleaningGuide, etc.) ─────────
const MultiLinkInput = ({
  label,
  values = [],
  onChange,
  registerPendingCheck,
}) => {
  const [inputVal, setInputVal] = useState("");
  const [urlError, setUrlError] = useState("");

  useEffect(() => {
    if (registerPendingCheck) {
      registerPendingCheck(() => {
        const trimmed = inputVal.trim();
        if (!trimmed) return null;
        if (!isValidUrl(trimmed)) {
          return `${label}: Please enter a valid URL (must start with http:// or https://) or clear the field`;
        }
        return `${label}: You have an unsaved URL. Please click Add or clear the field before submitting`;
      });
    }
  }, [inputVal, registerPendingCheck, label]);

  const handleAdd = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) {
      setUrlError("URL is required");
      return;
    }
    if (!isValidUrl(trimmed)) {
      setUrlError(
        "Please enter a valid URL (must start with http:// or https://)",
      );
      return;
    }
    if (values.includes(trimmed)) {
      setUrlError("This URL has already been added");
      return;
    }
    setUrlError("");
    setInputVal("");
    onChange([...values, trimmed]);
  };

  const handleRemove = (idx) => onChange(values.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm text-gray-700">{label}</label>
        {values.length > 0 && (
          <span className="text-xs text-[#115B9D] font-medium">
            {values.length} link{values.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      {values.length > 0 && (
        <div className="space-y-1.5">
          {values.map((url, i) => (
            <SavedUrlRow key={i} url={url} onRemove={() => handleRemove(i)} />
          ))}
        </div>
      )}
      <UrlInputRow
        value={inputVal}
        onChange={(e) => {
          setInputVal(e.target.value);
          if (urlError) setUrlError("");
        }}
        onAction={handleAdd}
        actionLabel="Add"
        urlError={urlError}
        placeholder="https://example.com/guide"
      />
    </div>
  );
};

// ─── PDF Upload Section (wiringDiagram, userManual, serviceManual) ─
// Modified: supports toggle between Upload PDF and Enter URL per field
const PdfUploadSection = ({
  label,
  fieldKey,
  urls = [],
  onAdd,
  onRemove,
  uploading,
}) => {
  const inputRef = useRef(null);
  // "upload" | "url"
  const [mode, setMode] = useState("upload");
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState("");

  const fileList = Array.isArray(urls) ? urls.filter(Boolean) : [];

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setUrlError("URL is required");
      return;
    }
    if (!isValidUrl(trimmed)) {
      setUrlError(
        "Please enter a valid URL (must start with http:// or https://)",
      );
      return;
    }
    if (fileList.includes(trimmed)) {
      setUrlError("This URL has already been added");
      return;
    }
    setUrlError("");
    setUrlInput("");
    // reuse onAdd logic: call parent's state update directly via a synthetic "url add"
    // We pass null files and handle it differently — so we call onAdd with a special flag
    // Actually we use a dedicated callback pattern: just bubble up via a separate prop isn't
    // available, so we fire onAdd with a special marker object
    onAdd(fieldKey, null, null, trimmed);
  };

  return (
    <div className="space-y-2">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <label className="block text-sm text-gray-700">{label}</label>
        {fileList.length > 0 && (
          <span className="text-xs text-[#115B9D] font-medium">
            {fileList.length} item{fileList.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Saved items list */}
      {fileList.length > 0 && (
        <div className="space-y-1.5">
          {fileList.map((url, i) => {
            const isUrlEntry =
              url.startsWith("http://") || url.startsWith("https://");
            const isUploadedFile =
              url.includes(environment.api) ||
              url.includes("/uploads/") ||
              url.includes("/filePath/");
            const fileName =
              typeof url === "string"
                ? url.split("/").pop() || `File ${i + 1}`
                : `File ${i + 1}`;

            return (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-2 bg-[#115B9D08] border border-[#115B9D]/15 rounded-xl"
              >
                <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0">
                  {/* Show link icon for URLs, pdf icon for uploaded files */}
                  {isUploadedFile && !url.startsWith("http") ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[#115B9D]"
                    >
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[#115B9D]"
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  )}
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-gray-700 truncate flex-1 hover:text-[#115B9D] hover:underline transition-colors"
                  title={url}
                >
                  {fileName}
                </a>
                <button
                  type="button"
                  onClick={() => onRemove(fieldKey, i)}
                  className="size-6 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors shrink-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Toggle: Upload PDF / Enter URL */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        <button
          type="button"
          onClick={() => {
            setMode("upload");
            setUrlError("");
            setUrlInput("");
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            mode === "upload"
              ? "bg-white text-[#115B9D] shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" x2="12" y1="3" y2="15" />
          </svg>
          Upload PDF
        </button>
        <button
          type="button"
          onClick={() => {
            setMode("url");
            setUrlError("");
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            mode === "url"
              ? "bg-white text-[#115B9D] shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          Enter URL
        </button>
      </div>

      {/* Upload mode */}
      {mode === "upload" && (
        <label className="cursor-pointer block">
          <div
            className={`border-2 border-dashed rounded-xl px-4 py-3 flex items-center gap-3 transition-all ${uploading === fieldKey ? "border-[#115B9D] bg-[#115B9D08]" : "border-gray-200 hover:border-[#115B9D] hover:bg-[#115B9D08]"}`}
          >
            <div className="size-8 rounded-lg bg-[#115b9d18] flex items-center justify-center shrink-0">
              {uploading === fieldKey ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#115B9D] animate-spin"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#115B9D]"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" x2="12" y1="3" y2="15" />
                </svg>
              )}
            </div>
            <div>
              <p className="text-sm text-[#115B9D] font-medium">
                {uploading === fieldKey ? "Uploading..." : `Upload ${label}`}
              </p>
              <p className="text-xs text-gray-600">
                PDF files only · Multiple allowed
              </p>
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="application/pdf"
            multiple
            disabled={uploading === fieldKey}
            onChange={(e) => onAdd(fieldKey, e.target.files, inputRef)}
          />
        </label>
      )}

      {/* URL mode */}
      {mode === "url" && (
        <div className="space-y-1">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">
                <LinkIcon />
              </div>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => {
                  setUrlInput(e.target.value);
                  if (urlError) setUrlError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddUrl();
                  }
                }}
                placeholder="https://example.com/document.pdf"
                className={`w-full border rounded-xl pl-8 pr-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${urlError ? "border-red-400" : "border-gray-200"}`}
              />
            </div>
            <button
              type="button"
              onClick={handleAddUrl}
              className="shrink-0 px-3 py-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-medium rounded-xl transition-colors flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add
            </button>
          </div>
          {urlError && <p className="text-xs text-red-400">{urlError}</p>}
          <p className="text-xs text-gray-600">
            Enter a valid URL and press Add or Enter
          </p>
        </div>
      )}
    </div>
  );
};

// ─── PDF / Link Tooltip Badge ─────────────────────────────────────
const PdfBadge = ({ pdfs = [] }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const badgeRef = useRef(null);

  const computeTooltipPosition = () => {
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    const tooltipWidth = 240;
    let left = rect.left;
    if (left + tooltipWidth > window.innerWidth - 12)
      left = window.innerWidth - tooltipWidth - 12;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 200) {
      setTooltipStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 8,
        left,
        width: tooltipWidth,
        zIndex: 99999,
      });
    } else {
      setTooltipStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left,
        width: tooltipWidth,
        zIndex: 99999,
      });
    }
  };

  const allPdfs = Array.isArray(pdfs) ? pdfs.filter(Boolean) : [];
  if (allPdfs.length === 0)
    return <span className="text-xs text-gray-600">—</span>;

  return (
    <div className="relative inline-flex" ref={badgeRef}>
      <span
        onMouseEnter={() => {
          computeTooltipPosition();
          setShowTooltip(true);
        }}
        onMouseLeave={() => setShowTooltip(false)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#115B9D17] text-[#115B9D] text-xs font-medium cursor-default select-none transition-all hover:bg-[#6b25f924] whitespace-nowrap"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
        {allPdfs.length} link{allPdfs.length !== 1 ? "s" : ""}
      </span>
      {showTooltip && (
        <div
          style={tooltipStyle}
          className="pointer-events-none animate-in fade-in duration-150"
        >
          <div
            className="absolute -top-1.5 left-4 size-3 bg-gray-900 rotate-45 rounded-sm"
            style={tooltipStyle.bottom ? { top: "auto", bottom: "-6px" } : {}}
          />
          <div className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl">
            <div className="px-3 py-2 border-b border-white/10">
              <span className="text-[11px] font-semibold text-white/60 uppercase tracking-wider">
                {allPdfs.length} attached
              </span>
            </div>
            <div className="px-2 py-1.5 space-y-0.5 max-h-44 overflow-y-auto">
              {allPdfs.map((url, i) => {
                const name =
                  typeof url === "string"
                    ? url.split("/").pop() || url
                    : `Item ${i + 1}`;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg"
                  >
                    <span className="size-4 rounded flex items-center justify-center bg-[#115B9D]/40 text-[#a78bfa] text-[10px] font-bold shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-xs text-white/80 truncate leading-tight">
                      {name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper: gather all links/pdfs from a product for the badge
const getAllProductLinks = (item) => {
  const arrayFields = [
    "installationManual",
    "wiringDiagram",
    "userManual",
    "serviceManual",
    "dailyUseGuide",
    "cleaningGuide",
    "illustratedParts",
    "troubleshooting",
    "partsStore",
  ];
  const urls = [];
  arrayFields.forEach((f) => {
    if (Array.isArray(item[f])) urls.push(...item[f]);
  });
  if (item.dataSheet) urls.push(item.dataSheet);
  if (item.brochure) urls.push(item.brochure);
  return urls.filter(Boolean);
};

// ─── Field config ─────────────────────────────────────────────────
const FIELD_CONFIG = [
  { key: "dataSheet", label: "Data Sheet", type: "singleLink" },
  { key: "brochure", label: "Brochure", type: "singleLink" },
  { key: "dailyUseGuide", label: "Daily Use Guide", type: "multiLink" },
  { key: "cleaningGuide", label: "Cleaning Guide", type: "multiLink" },
  { key: "illustratedParts", label: "Illustrated Parts", type: "multiLink" },
  { key: "troubleshooting", label: "Troubleshooting", type: "multiLink" },
  { key: "partsStore", label: "Parts Store", type: "multiLink" },
  {
    key: "installationManual",
    label: "Installation Manual",
    type: "pdfUpload",
  },
  { key: "wiringDiagram", label: "Wiring Diagram", type: "pdfUpload" },
  { key: "userManual", label: "User Manual", type: "pdfUpload" },
  { key: "serviceManual", label: "Service Manual", type: "pdfUpload" },
];

// ─── Main Component ───────────────────────────────────────────────
const ProductsIndex = () => {
  const [formPage, setFormPage] = useState(false);

  const [products, setProducts] = useState([]);
  const [manufactureListing, setManufactureListing] = useState([]);
  const [equpimentListing, setEqupmentListing] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [uploadingField, setUploadingField] = useState(null);
  const [total, setTotal] = useState(0);

  const [filters, setFilter] = useState({
    page: 1,
    count: 10,
    search: "",
    status: "",
  });

  const pendingCheckRegistry = useRef({});

  const makeRegisterPendingCheck = useCallback(
    (fieldKey) => (checkerFn) => {
      pendingCheckRegistry.current[fieldKey] = checkerFn;
    },
    [],
  );

  const fetchProducts = async (p = {}) => {
    loader(true);
    const params = {
      page: p.page ?? filters.page,
      count: p.count ?? filters.count,
      search: p.search ?? searchQuery,
      status: p.status ?? filters.status,
    };
    const query = new URLSearchParams(params).toString();
    await ApiClient.get(`product/listing?${query}`)
      .then((res) => {
        if (res?.success) {
          setProducts(res?.data?.products || res?.data || []);
          setTotal(res.data.total);
        }
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    fetchProducts();
    (fetchEquipment(), fetchManufacture());
  }, []);

  const fetchManufacture = async () => {
    try {
      const params = {
        page: 1,
        count: 100000,
        status: "active",
      };
      const query = new URLSearchParams(params).toString();
      const res = await ApiClient.get(`manufacturer/listing?${query}`);
      if (res?.success) {
        let data = res?.data?.data;
        if (data.length) {
          data = data.map((res) => {
            return { name: res.title, id: res.id };
          });
          data.sort((a, b) => a.name - b.name);
        }
        setManufactureListing(data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEquipment = async () => {
    try {
      const params = {
        page: 1,
        count: 100000,
        status: "active",
      };
      const query = new URLSearchParams(params).toString();
      const res = await ApiClient.get(`equipment-type/listing?${query}`);
      if (res?.success) {
        let data = res?.data?.data;
        if (data.length) {
          data = data.map((res) => {
            return { name: res.title, id: res.id };
          });
          data.sort((a, b) => a.name - b.name);
        }
        setEqupmentListing(data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10000 * 1024) {
      swal({
        text: "Image size must be less than 10MB",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      swal({
        text: "Please upload a valid image (JPG, JPEG, PNG, WEBP)",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    setImagePreview(URL.createObjectURL(file));
    loader(true);
    ApiClient.postFormData("upload/image", { file }).then((res) => {
      if (res.fileName) setForm((prev) => ({ ...prev, image: res.fileName }));
      loader(false);
    });
  };

  // ── PDF upload handler ── (also handles URL adds via directUrl param)
  const handlePdfAdd = async (
    fieldKey,
    fileList,
    inputRef,
    directUrl = null,
  ) => {
    // If a direct URL is passed (from URL mode), just append it to state
    if (directUrl) {
      setForm((prev) => ({
        ...prev,
        [fieldKey]: [...(prev[fieldKey] || []), directUrl],
      }));
      return;
    }

    if (!fileList || fileList.length === 0) return;
    const filesArray = Array.from(fileList);
    for (const file of filesArray) {
      if (file.type !== "application/pdf") {
        swal({
          text: `${file.name} is not a PDF file`,
          icon: "error",
          className: "smallModal",
        });
        return;
      }
    }
    setUploadingField(fieldKey);
    loader(true);
    try {
      const res = await ApiClient.postFormData("upload/multiple/documents", {
        files: filesArray,
      });
      const uploadedFiles = res?.fileDetails || [];
      if (res?.success && uploadedFiles.length > 0) {
        const urls = uploadedFiles
          .map((f) => `${environment.api}${f.filePath}`)
          .filter(Boolean);
        setForm((prev) => ({
          ...prev,
          [fieldKey]: [...(prev[fieldKey] || []), ...urls],
        }));
      } else {
        swal({
          text: "Upload failed. Please try again.",
          icon: "error",
          className: "smallModal",
        });
      }
    } catch (err) {
      console.error(err);
      swal({
        text: "Failed to upload file.",
        icon: "error",
        className: "smallModal",
      });
    }
    setUploadingField(null);
    loader(false);
    if (inputRef?.current) inputRef.current.value = "";
  };

  const handlePdfRemove = (fieldKey, index) => {
    setForm((prev) => {
      const arr = [...(prev[fieldKey] || [])];
      arr.splice(index, 1);
      return { ...prev, [fieldKey]: arr };
    });
  };

  const validate = () => {
    const errors = {};
    if (!form.productName.trim())
      errors.productName = "Product name is required";
    if (!form.equipmentTypeId)
      errors.equipmentTypeId = "Equipment type is required";
    if (!form.manufacturerId.trim())
      errors.manufacturerId = "Manufacturer is required";
    if (!form.model.trim()) errors.model = "Model is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkPendingLinkInputs = () => {
    const registry = pendingCheckRegistry.current;
    for (const fieldKey of Object.keys(registry)) {
      const checker = registry[fieldKey];
      if (typeof checker === "function") {
        const errorMsg = checker();
        if (errorMsg) {
          return errorMsg;
        }
      }
    }
    return null;
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setImagePreview(null);
    setFormErrors({});
    setIsEditMode(false);
    setEditingId(null);
    pendingCheckRegistry.current = {};
    setIsModalOpen(true);
    setFormPage(true);
  };

  const openEditModal = async (id) => {
    loader(true);
    await ApiClient.get(`product/detail?id=${id}`)
      .then((res) => {
        if (res?.success) {
          const d = res.data;
          setForm({
            productName: d.productName || "",
            equipmentTypeId: d.equipmentTypeId._id || "",
            manufacturerId: d.manufacturerId._id || "",
            model: d.model || "",
            modelNumber: Array.isArray(d.modelNumber)
              ? d.modelNumber.map(String)
              : d.modelNumber
                ? [String(d.modelNumber)]
                : [],
            keySpecifications: d.keySpecifications || [],
            description: d.description || "",
            image: d.image || "",
            dataSheet: d.dataSheet || "",
            brochure: d.brochure || "",
            wiringDiagram: d.wiringDiagram || [],
            userManual: d.userManual || [],
            serviceManual: d.serviceManual || [],
            dailyUseGuide: d.dailyUseGuide || [],
            cleaningGuide: d.cleaningGuide || [],
            illustratedParts: d.illustratedParts || [],
            troubleshooting: d.troubleshooting || [],
            partsStore: d.partsStore || [],
            installationManual: d?.installationManual || [],
          });
          setImagePreview(d.image ? methodModel.userImg(d.image) : null);
          setFormErrors({});
          setIsEditMode(true);
          setEditingId(id);
          pendingCheckRegistry.current = {};
          setIsModalOpen(true);
          setFormPage(true);
        }
      })
      .catch(console.error);
    loader(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
    setImagePreview(null);
    setFormErrors({});
    setUploadingField(null);
    pendingCheckRegistry.current = {};
    setFormPage(false);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const pendingLinkError = checkPendingLinkInputs();
    if (pendingLinkError) {
      swal({
        text: pendingLinkError,
        icon: "warning",
        className: "smallModal",
      });
      return;
    }
    loader(true);
    const payload = { ...form };
    const endpoint = isEditMode ? "product/edit" : "product/add";
    await ApiClient[isEditMode ? "put" : "post"](
      endpoint,
      isEditMode ? { ...payload, id: editingId } : payload,
    )
      .then((res) => {
        if (res?.success) {
          swal({
            text: `Product ${isEditMode ? "updated" : "added"} successfully.`,
            icon: "success",
            className: "smallModal",
          });
          closeModal();
          setFormPage(false);
          fetchProducts();
        }
      })
      .catch(console.error);
    loader(false);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This product will be permanently deleted.",
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      await ApiClient.delete(`product/delete?id=${id}`)
        .then(async (res) => {
          if (res?.success) {
            await fetchProducts();
            setFormPage(false);
            swal({
              text: "Product deleted successfully.",
              icon: "success",
              className: "smallModal",
            });
          }
        })
        .catch(console.error);
      loader(false);
    });
  };

  const filtered = (products || []).filter((p) => {
    const matchSearch =
      p.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.model || p.modelNumber)
        ?.toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchFilter = filterType === "All" || p.equipmentType === filterType;
    return matchSearch && matchFilter;
  });

  const uniqueTypes = [
    "All",
    ...new Set((products || []).map((p) => p.equipmentType).filter(Boolean)),
  ];

  const singleLinkFields = FIELD_CONFIG.filter((f) => f.type === "singleLink");
  const multiLinkFields = FIELD_CONFIG.filter((f) => f.type === "multiLink");
  const pdfUploadFields = FIELD_CONFIG.filter((f) => f.type === "pdfUpload");

  const handleFeaturedChange = (id, value) => {
    setProducts((prev) =>
      prev.map((p) =>
        (p.id || p?._id) === id ? { ...p, isFeatured: value } : p,
      ),
    );
    ApiClient.put("product/edit", { id, isFeatured: value });
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > Math.ceil(total / filters.count)) return;
    setFilter((prev) => ({
      ...prev,
      page: newPage,
    }));
    fetchProducts({ page: newPage });
  };

  const statusChange = (item, status) => {
    swal({
      title: "Are you sure?",
      text: `Do you want to ${status === "active" ? "activate" : "deactivate"} this Product?`,
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Yes"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put(`product/edit`, {
          id: item._id,
          status: status,
        });
        if (res?.success) {
          await fetchProducts();
          swal({
            text: `Product ${status === "active" ? "activated" : "deactivated"} successfully.`,
            icon: "success",
            className: "smallModal",
          });
        }
      } catch (err) {
        console.error(err);
      }
      loader(false);
    });
  };

  const changestatus = (e, key) => {
    setFilter((prev) => ({
      ...prev,
      [key]: e,
      page: 1,
    }));
    fetchProducts({ [key]: e, page: 1 });
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProducts({ search: searchQuery, page: 1 });
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const totalPages = Math.ceil(total / filters.count);
  const currentPage = Number(filters.page);

  const visiblePages = Array.from(
    { length: totalPages },
    (_, i) => i + 1,
  ).filter(
    (page) =>
      page >= Math.max(1, currentPage - 1) &&
      page <= Math.min(totalPages, currentPage + 1),
  );

  return (
    <Layout>
      {!formPage ? (
        <>
          <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
            {/* ─── Page Header ─── */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
              <div className="flex flex-col gap-1">
                <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
                  Products
                </h2>
                <p className="text-sm sm:text-md text-gray-600">
                  {total} product{total.length !== 1 ? "s" : ""} total
                </p>
              </div>
              <button
                onClick={openAddModal}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Product
              </button>
            </div>

            <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
              <div className="flex justify-between flex-wrap gap-3 mb-4">
                <div className="relative w-full sm:w-[350px]">
                  {/* Search Icon */}
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>

                  <input
                    type="text"
                    placeholder="Search by name, manufacturer, model..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl pl-9 pr-9 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                  />

                  {/* Clear (Cross) Icon */}
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <SelectDropdown
                    id="statusDropdown"
                    theme="search"
                    displayValue="name"
                    placeholder="All status"
                    intialValue={filters?.status}
                    result={(e) => {
                      changestatus(e.value, "status");
                    }}
                    isClearable={false}
                    crossIcon={filters?.status ? true : false}
                    options={shared.filterStatus}
                  />
                </div>
              </div>

              {/* ─── Table ─── */}

              {/* Desktop Table */}
              <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col style={{ width: "10%" }} /> {/* Actions */}
                    <col style={{ width: "26%" }} /> {/* Product */}
                    <col style={{ width: "14%" }} /> {/* Equipment */}
                    <col style={{ width: "16%" }} /> {/* Manufacturer */}
                    <col style={{ width: "12%" }} /> {/* Model */}
                    <col style={{ width: "12%" }} /> {/* Links */}
                    <col style={{ width: "10%" }} /> {/* Status */}
                  </colgroup>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {[
                        "Actions",
                        "Product",
                        "Equipment",
                        "Manufacturer",
                        "Model.",
                        "Links",
                        "Status",
                      ].map((h) => (
                        <th key={h} className="text-left py-3 px-3">
                          <span className="text-xs text-gray-700 font-medium truncate block">
                            {h}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-16">
                          {/* same as before */}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((item, index) => {
                        const allLinks = getAllProductLinks(item);
                        return (
                          <tr
                            key={item.id || index}
                            className="hover:bg-gray-50/60 transition-colors align-top"
                          >
                            {/* Actions */}
                            <td className="py-3 px-3 align-top">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => openEditModal(item.id)}
                                  title="Edit"
                                  className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item.id)}
                                  title="Delete"
                                  className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors shrink-0"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M3 6h18" />
                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                    <line x1="10" x2="10" y1="11" y2="17" />
                                    <line x1="14" x2="14" y1="11" y2="17" />
                                  </svg>
                                </button>
                              </div>
                            </td>

                            {/* Product */}
                            <td className="py-3 px-3 align-top overflow-hidden">
                              <div className="flex items-start gap-2.5 min-w-0">
                                <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center overflow-hidden shrink-0">
                                  {item.image ? (
                                    <img
                                      src={methodModel.userImg(item.image)}
                                      alt={item.productName}
                                      className="size-full object-cover rounded-xl"
                                    />
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="text-[#115B9D]"
                                    >
                                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                    </svg>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-gray-900 break-words">
                                    {item.productName}
                                  </p>
                                  {item.description && (
                                    <p
                                      dangerouslySetInnerHTML={{
                                        __html: item.description,
                                      }}
                                      className="text-[11px] text-gray-600 break-words leading-snug truncate cursor-pointer"
                                      title={item.description.replace(/<[^>]*>/g, "")}
                                    ></p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Equipment */}
                            <td className="py-3 px-3 align-top">
                              <p className="text-xs text-gray-800 truncate">
                                {item?.equipmentTypeId?.title}
                              </p>
                            </td>

                            {/* Manufacturer */}
                            <td className="py-3 px-3 align-top">
                              <p className="text-xs text-gray-800 truncate">
                                {item.manufacturerId?.title}
                              </p>
                            </td>

                            {/* Model */}
                            <td className="py-3 px-3 align-top">
                              <p className="text-xs text-gray-800 truncate">
                                {item.model || ""}
                              </p>
                            </td>

                            {/* Links */}
                            <td className="py-3 px-3 align-top overflow-hidden">
                              <PdfBadge pdfs={allLinks} />
                            </td>

                            {/* Status */}
                            <td className="py-3 px-3 align-top cursor-pointer">
                              {item.status === "active" ? (
                                <span
                                  onClick={() => statusChange(item, "deactive")}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#f0fdf4] text-[#008236] text-[10px] whitespace-nowrap"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="11"
                                    height="11"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="m9 12 2 2 4-4" />
                                  </svg>
                                  Active
                                </span>
                              ) : (
                                <span
                                  onClick={() => statusChange(item, "active")}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-[10px] whitespace-nowrap"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="11"
                                    height="11"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                  </svg>
                                  Deactive
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden">
                {filtered.length === 0 ? (
                  <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                    <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-gray-600"
                      >
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500">No products found</p>
                    <button
                      onClick={openAddModal}
                      className="text-[#115B9D] text-sm hover:underline"
                    >
                      Add your first product
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filtered.map((item, index) => {
                      const allLinks = getAllProductLinks(item);
                      return (
                        <div
                          key={item.id || index}
                          className="border border-gray-200 rounded-xl p-4 bg-white"
                        >
                          {/* Top row: Product info + Actions */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div>
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center overflow-hidden shrink-0">
                                  {item.image ? (
                                    <img
                                      src={methodModel.userImg(item.image)}
                                      alt={item.productName}
                                      className="size-full object-cover rounded-xl"
                                    />
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="text-[#115B9D]"
                                    >
                                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                    </svg>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {item.productName}
                                  </p>
                                  
                                </div>
                              </div>
                              {item.description && (
                                    <p
                                      dangerouslySetInnerHTML={{
                                        __html: item.description,
                                      }}
                                      className="text-xs text-gray-600 mt-3"
                                    ></p>
                                  )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => openEditModal(item.id)}
                                title="Edit"
                                className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="15"
                                  height="15"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(item.id)}
                                title="Delete"
                                className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="15"
                                  height="15"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 6h18" />
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                  <line x1="10" x2="10" y1="11" y2="17" />
                                  <line x1="14" x2="14" y1="11" y2="17" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Equipment */}
                          <div className="flex justify-between items-center py-1.5 border-t border-gray-100 font-extrabold">
                            <span className="text-xs text-gray-500">
                              Equipment
                            </span>
                            <p className="text-sm text-gray-800 text-right">
                              {item?.equipmentTypeId?.title || "—"}
                            </p>
                          </div>

                          {/* Manufacturer */}
                          <div className="flex justify-between items-center py-1.5 border-t border-gray-100 font-extrabold">
                            <span className="text-xs text-gray-500">
                              Manufacturer
                            </span>
                            <p className="text-sm text-gray-800 text-right">
                              {item.manufacturerId?.title || "—"}
                            </p>
                          </div>

                          {/* Model */}
                          <div className="flex justify-between items-center py-1.5 border-t border-gray-100 font-extrabold">
                            <span className="text-xs text-gray-500">Model</span>
                            <p className="text-sm text-gray-800 text-right">
                              {item.model || "—"}
                            </p>
                          </div>

                          {/* Links */}
                          <div className="flex justify-between items-center py-1.5 border-t border-gray-100 font-extrabold">
                            <span className="text-xs text-gray-500">Links</span>
                            <PdfBadge pdfs={allLinks} />
                          </div>

                          {/* Status */}
                          <div className="flex justify-between items-center py-1.5 border-t border-gray-100 font-extrabold">
                            <span className="text-xs text-gray-500">
                              Status
                            </span>
                            {item.status === "active" ? (
                              <span
                                onClick={() => statusChange(item, "deactive")}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-xs cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="m9 12 2 2 4-4" />
                                </svg>
                                Active
                              </span>
                            ) : (
                              <span
                                onClick={() => statusChange(item, "active")}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-xs cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                Deactive
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ─── Pagination ─── */}
              {total > filters.count && (
                <div className="flex flex-wrap gap-y-2 gap-x-5 items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Showing {(filters.page - 1) * filters.count + 1} to{" "}
                    {Math.min(filters.page * filters.count, total)} of {total}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                      className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                    >
                      Prev
                    </button>
                    {visiblePages.map((p) => (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`px-3 py-1.5 text-sm rounded-lg ${
                          currentPage === p
                            ? "bg-[#115B9D] text-white"
                            : "border"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={
                        filters.page === Math.ceil(total / filters.count)
                      }
                      className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
            <div className="">
              {/* Page Header */}
              <div className="flex items-center gap-3 mb-8">
                <button
                  type="button"
                  onClick={closeModal}
                  className="size-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-600"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="size-10 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#115B9D"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-[600] text-gray-800 tracking-tight">
                    {isEditMode ? "Edit Product" : "Add New Product"}
                  </h1>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {isEditMode
                      ? "Update the product details below"
                      : "Fill in the details to add a new product"}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {/* ── Image Upload ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-4">
                    Product Image
                  </p>
                  <label className="cursor-pointer">
                    <div
                      className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${imagePreview ? "border-[#115B9D] bg-[#115B9D17]" : "border-gray-200 hover:border-[#115B9D] hover:bg-[#115B9D17]"}`}
                    >
                      {imagePreview ? (
                        <div className="flex items-center gap-4">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="size-16 rounded-xl object-cover border border-gray-200"
                          />
                          <div className="text-left">
                            <p className="text-sm text-gray-700 font-medium">
                              Image selected
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              Click to change
                            </p>
                            <p className="text-xs text-gray-600">
                              Max 500KB · JPG, PNG, WEBP
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="size-10 rounded-full bg-[#115b9d29] flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#115B9D"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" x2="12" y1="3" y2="15" />
                            </svg>
                          </div>
                          <p className="text-sm text-[#115B9D]">
                            Click to upload image
                          </p>
                          <p className="text-xs text-gray-600">
                            Max 500KB · JPG, JPEG, PNG, WEBP
                          </p>
                        </div>
                      )}
                      <input
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImageChange}
                      />
                    </div>
                  </label>
                </div>

                {/* ── Product Info ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-5">
                    Product Info
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <FormInput
                        label="Product Name"
                        name="productName"
                        required
                        value={form.productName}
                        onChange={handleChange}
                        error={formErrors.productName}
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Equipment Type <span className="text-red-500">*</span>
                      </label>
                      <SelectDropdown
                        name="equipmentType"
                        theme="search"
                        placeholder="Select Type"
                        displayValue="name"
                        showPlaceHolder={false}
                        options={equpimentListing}
                        required
                        intialValue={form.equipmentTypeId}
                        result={(e) =>
                          setForm({ ...form, equipmentTypeId: e.value })
                        }
                        error={formErrors.equipmentTypeId}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <FormControl
                        type="editor"
                        label="Description"
                        name="description"
                        textarea
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e })}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Manufacturer <span className="text-red-500">*</span>
                      </label>
                      <SelectDropdown
                        name="Manufacturer"
                        theme="search"
                        placeholder="Select Manufacturer"
                        displayValue="name"
                        showPlaceHolder={false}
                        options={manufactureListing}
                        required
                        intialValue={form.manufacturerId}
                        result={(e) =>
                          setForm({ ...form, manufacturerId: e.value })
                        }
                        error={formErrors.manufacturerId}
                      />
                    </div>

                    <FormInput
                      label="Model"
                      name="model"
                      required
                      value={form.model}
                      onChange={handleChange}
                      error={formErrors.model}
                    />

                    <div className="sm:col-span-2">
                      <MultiTagInput
                        label="Model Numbers"
                        values={form.modelNumber}
                        onChange={(vals) =>
                          setForm((prev) => ({ ...prev, modelNumber: vals }))
                        }
                        placeholder="e.g. 631100045, 631100046 — press Enter or comma"
                        error={formErrors.modelNumber}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <MultiTagInput
                        label="Key Specifications"
                        values={form.keySpecifications}
                        onChange={(vals) =>
                          setForm((prev) => ({
                            ...prev,
                            keySpecifications: vals,
                          }))
                        }
                        placeholder="e.g. 4–6 valves, drop-in underbar — press Enter or comma"
                      />
                    </div>
                  </div>
                </div>

                {/* ── Single Links ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                      Single Links
                    </p>
                    <span className="text-xs text-gray-600 normal-case">
                      (one URL per field)
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {singleLinkFields.map((f) => (
                      <SingleLinkInput
                        key={f.key}
                        label={f.label}
                        value={form[f.key]}
                        onChange={(val) =>
                          setForm((prev) => ({ ...prev, [f.key]: val }))
                        }
                        registerPendingCheck={makeRegisterPendingCheck(f.key)}
                      />
                    ))}
                  </div>
                </div>

                {/* ── Multiple Links ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                      Multiple Links
                    </p>
                    <span className="text-xs text-gray-600 normal-case">
                      (add as many URLs as needed)
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {multiLinkFields.map((f) => (
                      <MultiLinkInput
                        key={f.key}
                        label={f.label}
                        values={form[f.key] || []}
                        onChange={(arr) =>
                          setForm((prev) => ({ ...prev, [f.key]: arr }))
                        }
                        registerPendingCheck={makeRegisterPendingCheck(f.key)}
                      />
                    ))}
                  </div>
                </div>

                {/* ── PDF Documents ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                      PDF Documents
                    </p>
                    <span className="text-xs text-gray-600 normal-case">
                      (upload PDF or enter URL)
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {pdfUploadFields.map((f) => (
                      <PdfUploadSection
                        key={f.key}
                        label={f.label}
                        fieldKey={f.key}
                        urls={form[f.key] || []}
                        onAdd={handlePdfAdd}
                        onRemove={handlePdfRemove}
                        uploading={uploadingField}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {/* ── Action Buttons ── */}
              <div className="flex gap-2 flex-wrap items-center justify-end mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {isEditMode ? (
                      <>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </>
                    ) : (
                      <path d="M12 5v14M5 12h14" />
                    )}
                  </svg>
                  {isEditMode ? "Update Product" : "Add Product"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default ProductsIndex;
